import { StopCircle, Visibility, VisibilityOff } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import Grid from "@mui/material/Unstable_Grid2";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format, Locale } from "date-fns";
import {
  enUS,
  enGB,
  nl,
  de,
  fr,
  es,
  it,
  pt,
  ru,
  pl,
  sv,
  da,
  fi,
  cs,
  ja,
  ko,
  zhCN,
} from "date-fns/locale";
import { FC, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  getTransferNightscoutDataProgress,
  stopTransferNightscoutData,
  transferNightscoutData,
  TransferNightscoutDataProgress,
  TransferNightscoutDataRequest,
} from "src/api/nigthscoutapi";
import { Seo } from "src/components/seo";
import { useEnvVars } from "src/contexts/envvars-context";
import { usePageView } from "src/hooks/use-page-view";

interface TransferFormData {
  source_url: string;
  source_api_secret: string;
  dest_url: string;
  dest_api_secret: string;
  data_types: string[];
  date_from?: string;
  date_to?: string;
}

const Page: FC = () => {
  usePageView();
  const { fullUrl, nsEnvVars } = useEnvVars();

  // Get browser locale for date pickers based on browser language
  const dateLocale = useMemo((): Locale => {
    const browserLang = navigator.language || navigator.languages?.[0] || "en";
    console.log("browserLang", browserLang);
    // Map browser language codes to date-fns locales
    const localeMap: Record<string, Locale> = {
      nl: nl,
      de: de,
      fr: fr,
      es: es,
      it: it,
      pt: pt,
      ru: ru,
      pl: pl,
      sv: sv,
      da: da,
      fi: fi,
      cs: cs,
      ja: ja,
      ko: ko,
      zh: zhCN,
      "en-GB": enGB,
      "en-US": enUS,
    };

    return localeMap[browserLang] || enUS;
  }, []);

  const [formData, setFormData] = useState<TransferFormData>({
    source_url: "",
    source_api_secret: "",
    dest_url: fullUrl || "",
    dest_api_secret: nsEnvVars?.API_SECRET || "",
    data_types: ["entries", "treatments", "profile"],
  });

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] =
    useState<TransferNightscoutDataProgress | null>(null);
  const [showSourcePassword, setShowSourcePassword] = useState(false);
  const [showDestPassword, setShowDestPassword] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  useEffect(() => {
    // Attempt to retrieve form data from localStorage
    const stored = localStorage.getItem("nightscout-transfer-formdata");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object") {
          setFormData((prev) => ({
            ...prev,
            ...parsed,
          }));
        }
      } catch (e) {
        // ignore parsing errors
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        source_url: "",
        source_api_secret: "",
        dest_url: fullUrl || "",
        dest_api_secret: nsEnvVars?.API_SECRET || "",
      }));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "nightscout-transfer-formdata",
      JSON.stringify(formData)
    );
  }, [formData]);

  useEffect(() => {
    let progressInterval: NodeJS.Timeout | undefined;
    progressInterval = setInterval(() => {
      getTransferNightscoutDataProgress().then((data) => {
        if (process.env.NODE_ENV === "development") {
          console.log(format(new Date(), "HH:mm:ss"), data);
        }
        if (!data.success) {
          clearInterval(progressInterval);
          toast.error("Error checking progress. " + data?.message ?? "");
        }
        if (data.success) {
          setProgress(data);
        }
        setLoading(Boolean(data.in_progress));
      });
    }, 3000);

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = undefined;
      }
    };
  }, []);

  const handleInputChange = (field: keyof TransferFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDataTypeChange = (dataType: string, checked: boolean) => {
    setFormData((prev) => {
      if (checked) {
        return {
          ...prev,
          data_types: [...prev.data_types, dataType],
        };
      } else {
        return {
          ...prev,
          data_types: prev.data_types.filter((type) => type !== dataType),
        };
      }
    });
  };

  const normalizeUrl = (url: string): string => {
    let normalized = url.trim();
    // Remove trailing slashes
    normalized = normalized.replace(/\/+$/, "");
    // Remove /api or /api/v1 from the end
    if (normalized.endsWith("/api") || normalized.endsWith("/api/v1")) {
      normalized = normalized.replace(/\/api(\/v1)?$/, "");
    }
    return normalized;
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.source_url) {
      toast.error("Source URL is required");
      return;
    }
    if (!formData.source_api_secret) {
      toast.error("Source Nightscout access token is required");
      return;
    }
    if (!formData.dest_url) {
      toast.error(
        "Destination URL is not available. Please ensure your Nightscout instance is configured."
      );
      return;
    }
    if (!formData.dest_api_secret) {
      toast.error(
        "Destination Nightscout Token is not available. Please ensure your Nightscout instance is configured."
      );
      return;
    }
    if (formData.data_types.length === 0) {
      toast.error("At least one data type must be selected");
      return;
    }
    if (!formData.date_from) {
      toast.error("At least start date must be selected");
      return;
    }
    if (
      formData.date_to &&
      new Date(parseInt(formData.date_from)) >
        new Date(parseInt(formData.date_to))
    ) {
      toast.error("Start date must be before end date");
      return;
    }

    setLoading(true);

    try {
      // Normalize URLs
      const normalizedSourceUrl = normalizeUrl(formData.source_url);
      const normalizedDestUrl = normalizeUrl(formData.dest_url);

      // Prepare request data
      const requestData: TransferNightscoutDataRequest = {
        source_url: normalizedSourceUrl,
        source_api_secret: formData.source_api_secret,
        dest_url: normalizedDestUrl,
        dest_api_secret: formData.dest_api_secret,
        data_types: formData.data_types,
        date_from: formData.date_from,
        date_to: formData.date_to,
      };

      const reponse = await transferNightscoutData(requestData);

      if (reponse.success) {
        toast.success("Data transfer started successfully");
      } else {
        toast.error(reponse.error || "Failed to start data transfer");
      }
    } catch (error) {
      console.error("Error starting data transfer:", error);
      toast.error("Failed to start data transfer: " + error);
    }
  };

  const handleStopTransfer = async () => {
    setStopLoading(true);
    const response = await stopTransferNightscoutData();
    if (response.success) {
      toast.success("Data transfer stopped successfully");
    } else {
      toast.error(response.error || "Failed to stop data transfer");
    }
    setStopLoading(false);
  };

  return (
    <>
      <Seo title="Transfer Nightscout Data" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Transfer Nightscout Data
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Transfer data from one Nightscout to another.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Form Card */}
            <Grid xs={12} lg={8}>
              <Card>
                <CardContent>
                  <Stack spacing={3}>
                    {/* Source Nightscout URL */}
                    <TextField
                      fullWidth
                      label="Source Nightscout URL"
                      value={formData.source_url}
                      onChange={(e) =>
                        handleInputChange("source_url", e.target.value)
                      }
                      placeholder="https://your-source-nightscout.herokuapp.com"
                      required
                      helperText="The URL of the source Nightscout instance"
                    />
                    <TextField
                      fullWidth
                      type={showSourcePassword ? "text" : "password"}
                      label="Source Nightscout Token"
                      value={formData.source_api_secret}
                      onChange={(e) =>
                        handleInputChange("source_api_secret", e.target.value)
                      }
                      required
                      helperText="Access token for the source Nightscout (with 'readable' permission)"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle source password visibility"
                              onClick={() =>
                                setShowSourcePassword(!showSourcePassword)
                              }
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showSourcePassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Destination Nightscout URL */}
                    <TextField
                      fullWidth
                      label="Destination Nightscout URL"
                      value={formData.dest_url}
                      onChange={(e) =>
                        handleInputChange("dest_url", e.target.value)
                      }
                      placeholder="https://your-dest-nightscout.herokuapp.com"
                      required
                      helperText="The URL of the destination Nightscout instance"
                    />
                    <TextField
                      fullWidth
                      type={showDestPassword ? "text" : "password"}
                      label="Destination Nightscout Token"
                      value={formData.dest_api_secret}
                      onChange={(e) =>
                        handleInputChange("dest_api_secret", e.target.value)
                      }
                      required
                      helperText="Access token for the destination Nightscout (with 'admin' permission)"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle destination password visibility"
                              onClick={() =>
                                setShowDestPassword(!showDestPassword)
                              }
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showDestPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Data Types */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Data Types
                      </Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.data_types.includes("entries")}
                              onChange={(e) =>
                                handleDataTypeChange(
                                  "entries",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Glucose measurements"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.data_types.includes(
                                "treatments"
                              )}
                              onChange={(e) =>
                                handleDataTypeChange(
                                  "treatments",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Treatments (boluses, carbs, etc.)"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.data_types.includes("profile")}
                              onChange={(e) =>
                                handleDataTypeChange(
                                  "profile",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Profiles"
                        />
                      </FormGroup>
                    </Box>

                    {/* Date Filters */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Date Range
                      </Typography>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={dateLocale}
                      >
                        <Stack direction="row" spacing={2}>
                          <DatePicker
                            label="Date From"
                            value={
                              formData.date_from
                                ? new Date(parseInt(formData.date_from))
                                : null
                            }
                            onChange={(newValue) =>
                              handleInputChange(
                                "date_from",
                                newValue?.getTime().toString() ?? ""
                              )
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                helperText: "Starts from 00:00",
                              },
                            }}
                          />
                          <DatePicker
                            label="Date To"
                            value={
                              formData.date_to
                                ? new Date(parseInt(formData.date_to))
                                : null
                            }
                            onChange={(newValue) =>
                              handleInputChange(
                                "date_to",
                                newValue?.getTime().toString() ?? ""
                              )
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                helperText:
                                  "Leave empty to transfer until current time",
                              },
                            }}
                          />
                        </Stack>
                      </LocalizationProvider>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Results Card */}
            <Grid xs={12} lg={4}>
              <Card>
                <CardContent>
                  <LoadingButton
                    variant="contained"
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={loading}
                    startIcon={<CloudUploadIcon />}
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Transfer Data
                  </LoadingButton>
                  <LoadingButton
                    variant="outlined"
                    onClick={handleStopTransfer}
                    loading={stopLoading}
                    disabled={progress?.completed}
                    startIcon={<StopCircle />}
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Stop Transfer
                  </LoadingButton>
                  {progress?.completed && (
                    <Stack spacing={2}>
                      <>
                        <Typography
                          variant="body2"
                          color="success.main"
                          fontWeight="bold"
                        >
                          Transfer Completed
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="body2">
                            {"Transfer started at: "}{" "}
                            {progress.start_datetime
                              ? new Date(
                                  progress.start_datetime
                                ).toLocaleString(navigator.language, {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                })
                              : ""}
                          </Typography>
                          <Typography variant="body2">
                            {"Transfer ended at: "}{" "}
                            {progress.end_datetime
                              ? new Date(progress.end_datetime).toLocaleString(
                                  navigator.language,
                                  { dateStyle: "short", timeStyle: "short" }
                                )
                              : ""}
                          </Typography>
                          <Typography variant="body2">
                            {"Entries transferred: "}{" "}
                            {progress.results?.entries?.count ?? 0}
                          </Typography>
                          <Typography variant="body2">
                            {"Treatments transferred: "}{" "}
                            {progress.results?.treatments?.count ?? 0}
                          </Typography>
                          <Typography variant="body2">
                            {"Profiles transferred: "}{" "}
                            {progress.results?.profile?.count ?? 0}
                          </Typography>
                          <Typography variant="body2">
                            {"Source URL: "} {progress.source_url}
                          </Typography>
                          <Typography variant="body2">
                            {"Destination URL: "} {progress.dest_url}
                          </Typography>
                          <Typography variant="body2">
                            {"From: "}{" "}
                            {progress.date_from
                              ? new Date(progress.date_from).toLocaleString(
                                  navigator.language,
                                  { dateStyle: "short", timeStyle: "short" }
                                )
                              : ""}
                          </Typography>
                          <Typography variant="body2">
                            {"Until: "}{" "}
                            {progress.date_to
                              ? new Date(progress.date_to).toLocaleString(
                                  navigator.language,
                                  { dateStyle: "short", timeStyle: "short" }
                                )
                              : ""}
                          </Typography>
                        </Stack>
                      </>
                    </Stack>
                  )}
                  {!progress?.completed && progress?.in_progress && (
                    <Stack spacing={2}>
                      <Typography variant="body2" color="info.main">
                        Transfer in progress...
                      </Typography>
                      <Typography variant="body2">
                        {" "}
                        {progress.results?.entries?.count ?? 0} entries
                        transferred
                      </Typography>
                      <Typography variant="body2">
                        {" "}
                        {progress.results?.treatments?.count ?? 0} treatments
                        transferred
                      </Typography>
                      <Typography variant="body2">
                        {" "}
                        {progress.results?.profile?.count ?? 0} profiles
                        transferred
                      </Typography>
                      <LinearProgress />
                    </Stack>
                  )}
                  {loading && !progress && (
                    <Typography variant="body2" color="text.secondary">
                      Loading...
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Page;
